import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/supabaseClient';
import { Input } from '@/components/ui/input';
import { useEffectiveMe } from '@/lib/effectiveMe';
import { hasCap } from '@/lib/capabilities';
import { normalizeLeaveMinimum, useSettingsHierarchy } from '@/lib/settingsHierarchy';
import SettingsLayout from '@/components/settings/SettingsLayout';
import SettingsHierarchy from '@/components/settings/SettingsHierarchy';
import SettingsLoadingState from '@/components/settings/SettingsLoadingState';
import SettingsErrorState from '@/components/settings/SettingsErrorState';
import SettingRow from '@/components/settings/SettingRow';
import SettingsConfirmDialog from '@/components/settings/SettingsConfirmDialog';
import SharedMinimumGroups from '@/components/settings/SharedMinimumGroups';

export default function ImpostazioniFerieFabbisogno() {
  const me = useEffectiveMe();
  const canEdit = hasCap(me, 'ferie.config.manage');
  const {
    companies,
    locationsByCompany,
    departmentsByLocation,
    loading: hierarchyLoading,
    error: hierarchyError,
    reload: reloadHierarchy,
  } = useSettingsHierarchy({ eagerDepartments: true });
  const [values, setValues] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState('');
  const [savedKey, setSavedKey] = useState('');
  const [configLoading, setConfigLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState('');
  const [saveErrors, setSaveErrors] = useState({});
  const [error, setError] = useState('');
  const [configReloadKey, setConfigReloadKey] = useState(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setConfigLoading(true);
      setError('');
      try {
        const configRows = await apiFetch('/api/ferie/fabbisogno-minimo');
        if (!mounted) return;
        const mappedValues = {};
        (configRows || []).forEach((item) => {
          const normalized = normalizeLeaveMinimum(item);
          mappedValues[`${normalized.locationId}:${normalized.departmentId}`] = normalized.minHeadcount;
        });
        setValues(mappedValues);
      } catch (loadError) {
        if (mounted) setError(loadError.message || 'Impossibile caricare la configurazione');
      } finally {
        if (mounted) setConfigLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [configReloadKey]);

  const beginEdit = (key) => {
    setEditingKey(key);
    setDraft(values[key] === undefined || values[key] === null ? '' : String(values[key]));
    setSavedKey('');
    setSaveErrors((current) => ({ ...current, [key]: '' }));
  };

  const cancelEdit = () => {
    if (editingKey) {
      setSaveErrors((current) => ({ ...current, [editingKey]: '' }));
    }
    setEditingKey(null);
    setDraft('');
  };

  const save = async (locationId, departmentId) => {
    const key = `${locationId}:${departmentId}`;
    if (!/^\d+$/.test(draft) || Number(draft) < 0 || Number(draft) > 10000) {
      setSaveErrors((current) => ({ ...current, [key]: 'Inserisci un numero intero da 0 a 10.000.' }));
      toast.error('Inserisci un numero intero da 0 a 10.000');
      return;
    }
    setSaving(key);
    setSaveErrors((current) => ({ ...current, [key]: '' }));
    try {
      const response = await apiFetch(`/api/ferie/fabbisogno-minimo/${locationId}/${departmentId}`, {
        method: 'PUT',
        body: JSON.stringify({ min_headcount: Number(draft) }),
      });
      const savedValue = Number(response.min_headcount);
      setValues((current) => ({ ...current, [key]: savedValue }));
      setEditingKey(null);
      setDraft('');
      setSavedKey(key);
      toast.success('Salvato', { description: 'Fabbisogno minimo aggiornato.' });
    } catch (saveError) {
      setSaveErrors((current) => ({ ...current, [key]: saveError.message || 'Impossibile salvare il fabbisogno minimo.' }));
      toast.error(saveError.message || 'Impossibile salvare il fabbisogno minimo');
    } finally {
      setSaving('');
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    const { key, locationId, departmentId } = deleteTarget;
    setDeleting(key);
    setSaveErrors((current) => ({ ...current, [key]: '' }));
    try {
      await apiFetch(`/api/ferie/fabbisogno-minimo/${locationId}/${departmentId}`, {
        method: 'DELETE',
      });
      setValues((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
      setSavedKey('');
      setDeleteTarget(null);
      toast.success('Configurazione rimossa', {
        description: 'Il reparto è rimasto intatto e puoi impostare nuovamente il valore.',
      });
    } catch (removeError) {
      setSaveErrors((current) => ({ ...current, [key]: removeError.message || 'Impossibile rimuovere la configurazione.' }));
      toast.error(removeError.message || 'Impossibile rimuovere la configurazione');
      throw removeError;
    } finally {
      setDeleting('');
    }
  };

  const loading = hierarchyLoading || configLoading;
  const pageError = hierarchyError || error;

  return (
    <SettingsLayout
      eyebrow="Impostazioni · Ferie"
      title="Fabbisogno minimo"
      description="Indica quante persone servono normalmente in ogni reparto o gruppo di locali."
      breadcrumbs={[{ label: 'Impostazioni', to: '/impostazioni' }]}
      current="Fabbisogno minimo"
      navigation={[{ to: '/impostazioni', label: 'Tutte le impostazioni' }]}
      testId="impostazioni-ferie-fabbisogno"
    >
      {!canEdit && <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Non hai il permesso per modificare questa configurazione.</div>}
      {loading && <SettingsLoadingState />}
      {!loading && pageError && (
        <SettingsErrorState
          message={pageError}
          onRetry={() => {
            reloadHierarchy();
            setConfigReloadKey((current) => current + 1);
          }}
        />
      )}
      {!loading && !pageError && (
        <>
          <SharedMinimumGroups
            companies={companies}
            locationsByCompany={locationsByCompany}
            departmentsByLocation={departmentsByLocation}
            canEdit={canEdit}
            onChanged={() => setConfigReloadKey((current) => current + 1)}
            compact
          />
          <SettingsHierarchy
            companies={companies}
            locationsByCompany={locationsByCompany}
            departmentsByLocation={departmentsByLocation}
            compact
            testId="fabbisogno-hierarchy"
            renderDepartment={({ location, department }) => {
              const key = `${location.id}:${department.id}`;
              const configured = values[key] !== undefined && values[key] !== null;
              const isEditing = editingKey === key;
              const isSaving = saving === key;
              const isDeleting = deleting === key;
              const rowError = saveErrors[key] || '';
              const original = configured ? String(values[key]) : '';
              return (
                <SettingRow
                  label={department.name}
                  description="Numero di persone"
                  value={`${department.activeAssignments} attivi · Minimo ${values[key]}`}
                  configured={configured}
                  editing={isEditing}
                  canEdit={canEdit}
                  dirty={draft !== original}
                  saving={isSaving || isDeleting}
                  saved={savedKey === key}
                  error={rowError}
                  editor={(
                    <Input
                      autoFocus
                      aria-label={`Numero di persone per ${department.name}`}
                      aria-describedby={rowError ? `fabbisogno-error-${department.id}` : undefined}
                      aria-invalid={rowError ? 'true' : undefined}
                      placeholder="Numero"
                      type="number"
                      min="0"
                      max="10000"
                      step="1"
                      required
                      value={draft}
                      onChange={(event) => {
                        setDraft(event.target.value);
                        if (rowError) setSaveErrors((current) => ({ ...current, [key]: '' }));
                      }}
                      disabled={isSaving || isDeleting}
                      className="w-full sm:w-28"
                    />
                  )}
                  onEdit={() => beginEdit(key)}
                  onSave={() => save(location.id, department.id)}
                  onCancel={cancelEdit}
                  onRemove={canEdit && configured && !isEditing
                    ? () => setDeleteTarget({
                      key,
                      locationId: location.id,
                      departmentId: department.id,
                      departmentName: department.name,
                    })
                    : undefined}
                  compact
                  testId={`fabbisogno-row-${department.id}`}
                />
              );
            }}
          />
        </>
      )}
      <SettingsConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => { if (!open && !deleting) setDeleteTarget(null); }}
        title="Rimuovere il fabbisogno minimo?"
        description={deleteTarget ? `Stai per rimuovere il fabbisogno minimo configurato per “${deleteTarget.departmentName}”.` : ''}
        consequences="Il valore verrà rimosso, ma il reparto rimarrà intatto. Potrai impostare nuovamente il fabbisogno in qualsiasi momento."
        confirmLabel="Rimuovi"
        cancelLabel="Annulla"
        destructive
        onConfirm={remove}
      />
    </SettingsLayout>
  );
}